import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Phone, CheckCircle2, ShieldCheck, Zap, Sparkles } from "lucide-react";
import ReferralCard from "@/components/travel/ReferralCard";
import CreditHistory from "@/components/travel/CreditHistory";
import PushSubscribeCard from "@/components/travel/PushSubscribeCard";

export default function Settings() {
  const { user, reload } = useAuth();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("input");
  const [quota, setQuota] = useState(null);
  const [connect, setConnect] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/subscriptions/quota").then(r => setQuota(r.data)).catch(()=>{});
    api.get("/stripe/connect/status").then(r => setConnect(r.data)).catch(()=>{});
  }, [user]);

  if (!user) return <div className="p-8 text-center text-slate-400">Connectez-vous.</div>;

  const startPhone = async () => {
    try { await api.post("/verify/phone/start", { phone }); toast.success("Code envoyé par email"); setStep("code"); }
    catch { toast.error("Erreur"); }
  };
  const confirmPhone = async () => {
    try { await api.post("/verify/phone/confirm", { phone, code }); toast.success("Téléphone vérifié !"); setStep("input"); setPhone(""); setCode(""); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const subscribe = async (plan) => {
    try { const r = await api.post("/subscriptions/checkout", { plan, origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch { toast.error("Erreur"); }
  };
  const startConnect = async () => {
    try { const r = await api.post("/stripe/connect/onboard", { origin_url: window.location.origin }); window.location.href = r.data.url; }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <h1 className="font-display text-3xl font-bold">Paramètres</h1>

      <ReferralCard />
      <CreditHistory />
      <PushSubscribeCard />

      <section className="glass-strong rounded-2xl p-6">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Crown size={16} className="text-[#F59E0B]"/> Abonnement</h2>
        {quota?.plan ? (
          <p className="mt-3 text-sm text-slate-300">Plan actif : <b>{quota.plan}</b> — {quota.unlimited ? "déblocages illimités" : `${quota.remaining_today} déblocage(s) restant(s) aujourd'hui`}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <button data-testid="sub-medium" onClick={()=>subscribe("medium")} className="btn-ghost-glass p-4 rounded-xl text-left">
              <div className="font-semibold">Medium — 20€/mois</div>
              <div className="text-xs text-slate-400 mt-1">10 déblocages par jour</div>
            </button>
            <button data-testid="sub-premium" onClick={()=>subscribe("premium")} className="btn-coral p-4 rounded-xl text-left text-white">
              <div className="font-semibold flex items-center gap-1">Premium — 30€/mois <Zap size={12}/></div>
              <div className="text-xs opacity-90 mt-1">Déblocages illimités</div>
            </button>
          </div>
        )}
      </section>

      <section className="glass-strong rounded-2xl p-6">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Phone size={16} className="text-[#F59E0B]"/> Vérification téléphone</h2>
        {user.badges?.includes("phone_verified") ? (
          <p className="mt-3 text-sm text-emerald-400 flex items-center gap-2"><CheckCircle2 size={14}/> Vérifié : {user.phone}</p>
        ) : step === "input" ? (
          <div className="mt-3 flex gap-2">
            <input data-testid="settings-phone" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+33 6 12 34 56 78"
              className="flex-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none focus:border-[#FF6B6B]"/>
            <button data-testid="settings-phone-send" onClick={startPhone} className="btn-coral px-4 py-2 rounded-lg text-sm">Envoyer code</button>
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <input data-testid="settings-phone-code" value={code} onChange={e=>setCode(e.target.value)} placeholder="Code à 6 chiffres"
              className="flex-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none focus:border-[#FF6B6B]"/>
            <button data-testid="settings-phone-confirm" onClick={confirmPhone} className="btn-coral px-4 py-2 rounded-lg text-sm">Confirmer</button>
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-2">Le code est envoyé par email pour la V1.</p>
      </section>

      <section className="glass-strong rounded-2xl p-6">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><ShieldCheck size={16} className="text-[#F59E0B]"/> Stripe Connect (voyageur)</h2>
        {connect?.connected ? (
          <p className="mt-3 text-sm text-emerald-400 flex items-center gap-2"><CheckCircle2 size={14}/> Compte Connect actif · payouts activés</p>
        ) : (
          <>
            <p className="mt-3 text-sm text-slate-300">Connectez votre compte Stripe pour recevoir automatiquement les fonds des séquestres.</p>
            <button data-testid="settings-connect" onClick={startConnect} className="mt-3 btn-coral px-4 py-2 rounded-lg text-sm">Ouvrir l'onboarding Stripe</button>
          </>
        )}
      </section>
      <section className="glass-strong rounded-2xl p-6">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Sparkles size={16} className="text-[#F59E0B]"/> Avatar AI</h2>
        <div className="mt-3 flex items-center gap-4">
          {user.picture ? (
            <img data-testid="settings-avatar-img" src={user.picture} alt="avatar" className="w-16 h-16 rounded-full object-cover border border-slate-700"/>
          ) : (
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center text-2xl font-bold">{user.name?.[0]}</div>
          )}
          <button data-testid="settings-gen-avatar" onClick={async ()=>{
            try { toast.info("Génération de votre avatar…"); await api.post("/ai/avatar"); toast.success("Avatar généré"); await reload(); }
            catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
          }} className="btn-coral px-4 py-2 rounded-lg text-sm">Générer mon avatar (Nano Banana)</button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Une image unique générée par IA à partir de votre nom.</p>
      </section>
    </div>
  );
}
